'use client';

import {
  type ComponentProps,
  type ComponentType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  type DrawerContent,
  type DrawerDescription,
  type DrawerFooter,
  type DrawerOverlay,
  type DrawerTitle,
  type Drawer as DrawerUI,
} from '@/components/ui/Drawer/components/DrawerUI';
import { create } from '@/libs';

export type DrawerOptions = {
  content?: ReactNode;
  Component?: ComponentType | ReactElement;

  title?: ReactNode;
  translateTitle?: boolean;
  description?: ReactNode;
  translateDescription?: boolean;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  hasHeader?: boolean;

  drawerOptions?: Omit<ComponentProps<typeof DrawerUI>, 'open' | 'onOpenChange'>;
  drawerOverlayOptions?: ComponentProps<typeof DrawerOverlay>;
  drawerContentOptions?: ComponentProps<typeof DrawerContent>;
  drawerHeaderOptions?: HTMLAttributes<HTMLDivElement>;
  drawerTitleOptions?: ComponentProps<typeof DrawerTitle>;
  drawerDescriptionOptions?: ComponentProps<typeof DrawerDescription>;
  drawerFooterOptions?: ComponentProps<typeof DrawerFooter>;

  hardClose?: boolean;
  priority?: number;
  onCloseOptional?: () => void;
  showPreventClose?: boolean;
  onPreventClose?: () => ReactNode;
  drawerType?: string;
};

export type DrawerData = DrawerOptions & {
  id: string;
  openedAt: number;
  isOpen: boolean;
  tempDisablePreventClose?: boolean;
};

type ReplaceDrawerArg = DrawerOptions | ((currentDrawer: DrawerData) => DrawerOptions);

type DrawerStore = {
  drawers: DrawerData[];
  openDrawer: (opts: DrawerOptions) => string;
  setDrawerOpen: (id: string, open: boolean) => void;
  removeDrawer: (id: string) => void;
  closeCurrentDrawer: () => void;
  replaceDrawer: (newDrawer: ReplaceDrawerArg) => void;
  showPreventClose?: boolean;
  preventingComponent?: ReactNode;
  updateShouldCallPreventClose: (status: boolean) => void;
  clearPreventingComponent: () => void;
  forceCloseDrawerWithPreventClear: () => void;
};

const compare = (drawerX: DrawerData, drawerY: DrawerData) =>
  (drawerY.priority ?? 0) - (drawerX.priority ?? 0) ||
  drawerY.openedAt - drawerX.openedAt;

export const useDrawerStore = create<DrawerStore>((set, get) => ({
  drawers: [],
  preventingComponent: undefined,

  openDrawer: (opts) => {
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

    const drawer: DrawerData = {
      id,
      openedAt: Date.now(),
      isOpen: true,
      priority: opts.priority ?? 0,
      hardClose: opts.hardClose ?? false,
      ...opts,
    };

    set((state) => ({ drawers: [...state.drawers, drawer].sort(compare) }));
    return id;
  },

  setDrawerOpen: (id, open) =>
    set((state) => {
      const showPreventClose = get().showPreventClose;
      const drawer = state.drawers.find((d) => d.id === id);
      if (!drawer) return state;

      if (!open) {
        if (
          drawer.onPreventClose &&
          (showPreventClose || drawer.showPreventClose) &&
          !drawer.tempDisablePreventClose
        ) {
          const node = drawer.onPreventClose();
          return { preventingComponent: node };
        }

        return {
          drawers: state.drawers.map((d) => (d.id === id ? { ...d, isOpen: false } : d)),
        };
      }

      return {
        drawers: state.drawers.map((d) => (d.id === id ? { ...d, isOpen: true } : d)),
      };
    }),

  removeDrawer: (id) => {
    const showPreventClose = get().showPreventClose;
    const drawer = get().drawers.find((d) => d.id === id);
    if (!drawer) return;

    if (
      drawer.onPreventClose &&
      (showPreventClose || drawer.showPreventClose) &&
      !drawer.tempDisablePreventClose
    ) {
      const node = drawer.onPreventClose();
      set({ preventingComponent: node });
      return;
    }

    drawer.onCloseOptional?.();
    set((state) => ({
      drawers: state.drawers.filter((d) => d.id !== id),
    }));
  },

  closeCurrentDrawer: () => {
    const showPreventClose = get().showPreventClose;
    const drawer = get().drawers[0];
    if (!drawer) return;

    if (
      drawer.onPreventClose &&
      (showPreventClose || drawer.showPreventClose) &&
      !drawer.tempDisablePreventClose
    ) {
      const node = drawer.onPreventClose();
      set({ preventingComponent: node });
      return;
    }

    drawer.onCloseOptional?.();
    set((state) => {
      const [, ...rest] = state.drawers;
      return { drawers: rest };
    });
  },

  replaceDrawer: (newDrawer) => {
    set((state) => {
      const currentDrawer = state.drawers[0];
      if (!currentDrawer) return state;

      const updatedDrawers = state.drawers.map((drawer, index) =>
        index === 0 ? { ...drawer, isOpen: false } : drawer,
      );

      setTimeout(() => {
        const resolvedDrawer =
          typeof newDrawer === 'function' ? newDrawer(currentDrawer) : newDrawer;

        const id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2);

        const drawer: DrawerData = {
          id,
          openedAt: Date.now(),
          isOpen: true,
          priority: resolvedDrawer.priority ?? 0,
          hardClose: resolvedDrawer.hardClose ?? false,
          ...resolvedDrawer,
        };

        set((next) => ({
          drawers: [...next.drawers, drawer].sort(compare),
        }));
      }, 350);

      return { drawers: updatedDrawers };
    });
  },

  updateShouldCallPreventClose: (status: boolean) => {
    set({ showPreventClose: status });
  },

  clearPreventingComponent: () => {
    set((state) => ({
      preventingComponent: undefined,
      drawers: state.drawers.map((d, index) =>
        index === 0 ? { ...d, tempDisablePreventClose: true } : d,
      ),
    }));

    setTimeout(() => {
      set((state) => ({
        drawers: state.drawers.map((d, index) =>
          index === 0 ? { ...d, tempDisablePreventClose: false } : d,
        ),
      }));
    }, 100);
  },

  forceCloseDrawerWithPreventClear: () => {
    const drawer = get().drawers[0];
    if (!drawer) return;

    drawer.onCloseOptional?.();

    set((state) => ({
      drawers: state.drawers.map((d, i) => (i === 0 ? { ...d, isOpen: false } : d)),
      preventingComponent: undefined,
    }));

    setTimeout(() => {
      set((state) => {
        const [, ...rest] = state.drawers;
        return { drawers: rest };
      });
    }, 300);
  },
}));
